import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { User, MapPin, Plus, Factory, FileText, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { useTier } from '../context/TierContext';

const AUSTRALIAN_STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];

// Helper function to generate operator code from name
const generateOperatorCode = (name) => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  let code = '';
  if (parts.length >= 2) {
    // First letter of first name + first letter of last name
    code = (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  } else if (parts.length === 1) {
    // First two letters of name
    code = parts[0].substring(0, 2).toUpperCase();
  }
  // Add random 3 digits
  const randomNum = Math.floor(100 + Math.random() * 900);
  return code + randomNum;
};

// Helper function to generate a random 4-digit PIN
const generateAccessPin = () => {
  return Math.floor(1000 + Math.random() * 9000).toString();
};

const OperatorEditDrawer = ({
  open,
  onOpenChange,
  operator,
  suppliers,
  equipmentTypes,
  onSubmit,
  onSupplierCreate,
  defaultSupplierId = null
}) => {
  const { hasFeature } = useTier();
  const hasQuotes = hasFeature('quotes');

  const [form, setForm] = useState({
    name: '',
    supplier_id: '',
    role: 'operator',
    equipment_types: [],
    is_available: true,
    phone: '',
    email: '',
    suburb: '',
    postcode: '',
    state: '',
    mobile_enabled: false,
    mobile_pin: '',
    operator_code: '',
    mobile_quotes_enabled: false
  });

  // Quick Add Supplier Dialog
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [supplierForm, setSupplierForm] = useState({
    company_name: '',
    contact_name: '',
    phone: '',
    email: ''
  });
  
  // Email warning dialog
  const [emailWarningOpen, setEmailWarningOpen] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);

  useEffect(() => {
    if (operator) {
      setForm({
        name: operator.name || '',
        supplier_id: operator.supplier_id || '',
        role: operator.role || 'operator',
        equipment_types: operator.equipment_types || [],
        is_available: operator.is_available !== false,
        phone: operator.phone || '',
        email: operator.email || '',
        suburb: operator.suburb || '',
        postcode: operator.postcode || '',
        state: operator.state || '',
        mobile_enabled: operator.mobile_enabled || false,
        mobile_pin: operator.mobile_pin || '',
        operator_code: operator.operator_code || '',
        mobile_quotes_enabled: operator.mobile_quotes_enabled || false
      });
    } else {
      setForm({
        name: '',
        supplier_id: defaultSupplierId || '',
        role: 'operator',
        equipment_types: [],
        is_available: true,
        phone: '',
        email: '',
        suburb: '',
        postcode: '',
        state: '',
        mobile_enabled: false,
        mobile_pin: '',
        operator_code: '',
        mobile_quotes_enabled: false
      });
    }
  }, [operator, defaultSupplierId, open]);

  const handleSupplierSelect = (value) => {
    if (value === '__add_new__') {
      setSupplierForm({ company_name: '', contact_name: '', phone: '', email: '' });
      setSupplierDialogOpen(true);
    } else {
      setForm({ ...form, supplier_id: value });
    }
  };

  const handleCreateSupplier = async (e) => {
    e.preventDefault();
    if (onSupplierCreate) {
      const newSupplier = await onSupplierCreate(supplierForm);
      if (newSupplier) {
        setForm({ ...form, supplier_id: newSupplier.id });
        setSupplierDialogOpen(false);
      }
    }
  };

  const toggleEquipmentType = (type) => {
    setForm(prev => ({
      ...prev,
      equipment_types: prev.equipment_types.includes(type)
        ? prev.equipment_types.filter(t => t !== type)
        : [...prev.equipment_types, type]
    }));
  };

  const toggleAllEquipmentTypes = () => {
    const allTypeNames = equipmentTypes.map(et => et.name);
    setForm(prev => {
      const allSelected = allTypeNames.every(name => prev.equipment_types.includes(name));
      return {
        ...prev,
        equipment_types: allSelected ? [] : allTypeNames
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.supplier_id) {
      return; // Don't submit without supplier
    }
    
    // Capture form data immediately to avoid any race conditions
    const formDataToSubmit = { ...form };
    
    // Check if mobile is enabled but no valid email
    const isValidEmail = formDataToSubmit.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formDataToSubmit.email);
    if (formDataToSubmit.mobile_enabled && !isValidEmail) {
      // Show warning dialog
      setPendingSubmit({ form: formDataToSubmit, operator });
      setEmailWarningOpen(true);
      return;
    }
    
    onSubmit(formDataToSubmit, operator);
  };
  
  const handleConfirmWithoutEmail = () => {
    if (pendingSubmit) {
      onSubmit(pendingSubmit.form, pendingSubmit.operator);
    }
    setEmailWarningOpen(false);
    setPendingSubmit(null);
  };
  
  const handleCancelEmailWarning = () => {
    setEmailWarningOpen(false);
    setPendingSubmit(null);
  };

  const isEditing = !!operator;
  const isFormValid = form.name && form.supplier_id;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-[800px] sm:max-w-[800px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading text-xl flex items-center gap-2">
              <User className="h-5 w-5 text-green-500" />
              {isEditing ? 'Edit Operator' : 'Add New Operator'}
            </SheetTitle>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-4" data-testid="operator-form">
            <div className="space-y-2">
              <Label className="label-overline">Supplier (Employer) *</Label>
              <Select value={form.supplier_id || ''} onValueChange={handleSupplierSelect}>
                <SelectTrigger data-testid="operator-supplier-select">
                  <SelectValue placeholder="Select supplier" />
                </SelectTrigger>
                <SelectContent>
                  {onSupplierCreate && (
                    <>
                      <SelectItem value="__add_new__" className="text-[#ffd108] font-medium">
                        <span className="flex items-center gap-2">
                          <Plus className="h-4 w-4" />
                          ADD NEW SUPPLIER
                        </span>
                      </SelectItem>
                      {suppliers.length > 0 && <div className="border-t border-border my-1" />}
                    </>
                  )}
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {suppliers.length === 0 && (
                <p className="text-xs text-amber-500">
                  No suppliers found. Please add a supplier first.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="label-overline">Full Name *</Label>
              <Input
                data-testid="operator-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="John Smith"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-overline">Role</Label>
                <Select
                  value={form.role}
                  onValueChange={(value) => setForm({ ...form, role: value })}
                >
                  <SelectTrigger data-testid="operator-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operator">Operator</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Available</Label>
                <div className="flex items-center h-10">
                  <Switch
                    checked={form.is_available}
                    onCheckedChange={(checked) => setForm({ ...form, is_available: checked })}
                    data-testid="operator-available-switch"
                  />
                  <span className="ml-2 text-sm">
                    {form.is_available ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-overline">Phone</Label>
                <Input
                  data-testid="operator-phone-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+1 234 567 890"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Email</Label>
                <Input
                  data-testid="operator-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="john@email.com"
                />
              </div>
            </div>

            {/* Location Section */}
            <div className="space-y-2">
              <Label className="label-overline flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                Location (for smart scheduling)
              </Label>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <Input
                    value={form.suburb}
                    onChange={(e) => setForm({ ...form, suburb: e.target.value })}
                    placeholder="Suburb"
                  />
                </div>
                <div>
                  <Input
                    value={form.postcode}
                    onChange={(e) => setForm({ ...form, postcode: e.target.value })}
                    placeholder="Postcode"
                    maxLength={4}
                  />
                </div>
                <div>
                  <Select
                    value={form.state}
                    onValueChange={(value) => setForm({ ...form, state: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="State" />
                    </SelectTrigger>
                    <SelectContent>
                      {AUSTRALIAN_STATES.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="label-overline">Can Operate</Label>
                {equipmentTypes.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-muted-foreground hover:text-foreground"
                    onClick={toggleAllEquipmentTypes}
                  >
                    {equipmentTypes.every(et => form.equipment_types.includes(et.name)) ? 'Clear All' : 'Select All'}
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {equipmentTypes.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No asset types available. Add them in Data Management.</p>
                ) : (
                  equipmentTypes.map((et) => (
                    <Badge
                      key={et.id || et.name}
                      variant={form.equipment_types.includes(et.name) ? 'default' : 'outline'}
                      className={`cursor-pointer transition-colors ${
                        form.equipment_types.includes(et.name) 
                          ? 'bg-[#ffd108] text-black hover:bg-[#E5B93D]' 
                          : 'hover:bg-muted'
                      }`}
                      onClick={() => toggleEquipmentType(et.name)}
                    >
                      {et.name}
                    </Badge>
                  ))
                )}
              </div>
            </div>

            {/* Mobile Access Section */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="label-overline">Mobile App Access</Label>
                  <p className="text-xs text-muted-foreground">Allow operator to use the mobile app</p>
                </div>
                <Switch
                  checked={form.mobile_enabled}
                  onCheckedChange={(checked) => {
                    if (checked && !form.operator_code && !form.mobile_pin) {
                      // Auto-generate operator code and PIN when enabling mobile access
                      setForm({ 
                        ...form, 
                        mobile_enabled: checked,
                        operator_code: generateOperatorCode(form.name),
                        mobile_pin: generateAccessPin()
                      });
                    } else {
                      setForm({ ...form, mobile_enabled: checked });
                    }
                  }}
                  data-testid="operator-mobile-switch"
                />
              </div>
              
              {form.mobile_enabled && (
                <div className="grid grid-cols-2 gap-4 pl-4 border-l-2 border-[#ffd108]">
                  <div className="space-y-2">
                    <Label className="label-overline">Access Code *</Label>
                    <Input
                      value={form.operator_code}
                      onChange={(e) => setForm({ ...form, operator_code: e.target.value.toUpperCase() })}
                      placeholder="JH001"
                      className="font-mono uppercase"
                      data-testid="operator-code-input"
                    />
                    <p className="text-xs text-muted-foreground">Used to access operator portal</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-overline">Access PIN *</Label>
                    <Input
                      type="password"
                      value={form.mobile_pin}
                      onChange={(e) => setForm({ ...form, mobile_pin: e.target.value })}
                      placeholder="4-6 digits"
                      maxLength={6}
                      data-testid="operator-pin-input"
                    />
                    <p className="text-xs text-muted-foreground">4-6 digit PIN</p>
                  </div>
                </div>
              )}

              {/* Quoting Access - Only show if tenant has quotes feature and mobile is enabled */}
              {form.mobile_enabled && hasQuotes && (
                <div className="pl-4 border-l-2 border-[#ffd108]">
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-500/20 rounded-lg">
                        <FileText className="h-4 w-4 text-blue-500" />
                      </div>
                      <div>
                        <Label className="label-overline">Quoting Access</Label>
                        <p className="text-xs text-muted-foreground">Allow creating and managing quotes on mobile</p>
                      </div>
                    </div>
                    <Switch
                      checked={form.mobile_quotes_enabled}
                      onCheckedChange={(checked) => setForm({ ...form, mobile_quotes_enabled: checked })}
                      data-testid="operator-mobile-quotes-switch"
                    />
                  </div>
                </div>
              )}
            </div>

            <SheetFooter className="pt-4 border-t mt-6">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt" data-testid="operator-submit-btn" disabled={!isFormValid}>
                {isEditing ? 'Update' : 'Add'} Operator
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* Quick Add Supplier Dialog */}
      {onSupplierCreate && (
        <Dialog open={supplierDialogOpen} onOpenChange={setSupplierDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Factory className="h-5 w-5 text-[#ffd108]" />
                Quick Add Supplier
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateSupplier} className="space-y-4">
              <div className="space-y-2">
                <Label className="label-overline">Company Name *</Label>
                <Input
                  value={supplierForm.company_name}
                  onChange={(e) => setSupplierForm({ ...supplierForm, company_name: e.target.value })}
                  placeholder="ABC Equipment Hire"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Contact Name *</Label>
                <Input
                  value={supplierForm.contact_name}
                  onChange={(e) => setSupplierForm({ ...supplierForm, contact_name: e.target.value })}
                  placeholder="John Smith"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">Phone</Label>
                  <Input
                    value={supplierForm.phone}
                    onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
                    placeholder="+1 234 567 890"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Email</Label>
                  <Input
                    type="email"
                    value={supplierForm.email}
                    onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
                    placeholder="john@supplier.com"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setSupplierDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-volt">
                  Create Supplier
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Email Warning Dialog */}
      <Dialog open={emailWarningOpen} onOpenChange={setEmailWarningOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              No Email Address
            </DialogTitle>
            <DialogDescription>
              This operator doesn't have a valid email address. They won't be able to receive their login details automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Alert className="border-amber-500/50 bg-amber-500/10">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <AlertDescription className="text-sm">
                Would you like to add an email address so the operator can receive their Operator Code and Access PIN?
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={handleConfirmWithoutEmail}>
              Continue Without Email
            </Button>
            <Button type="button" className="btn-volt" onClick={handleCancelEmailWarning}>
              Add Email Address
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OperatorEditDrawer;
